import { useId, type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import { cx } from '../internal/cx';
import styles from './Form.module.css';

export interface SwitchProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'role' | 'aria-checked'
> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Visible label. Without it, pass aria-label. */
  label?: ReactNode;
  description?: ReactNode;
  size?: 'sm' | 'md';
  /** Put the label before the switch. */
  labelPosition?: 'start' | 'end';
  ref?: Ref<HTMLButtonElement>;
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  size = 'md',
  labelPosition = 'end',
  disabled,
  className,
  id: idProp,
  ref,
  ...rest
}: SwitchProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const descriptionId = description ? `${id}-desc` : undefined;

  const control = (
    <button
      ref={ref}
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-describedby={descriptionId}
      disabled={disabled}
      className={cx(styles.switch, styles[`switch-${size}`])}
      onClick={() => onCheckedChange(!checked)}
      {...rest}
    >
      <span className={styles.switchThumb} aria-hidden="true" />
    </button>
  );

  if (label == null) return <span className={className}>{control}</span>;

  return (
    <div
      className={cx(
        styles.switchRow,
        labelPosition === 'start' && styles.switchRowStart,
        disabled && styles.choiceDisabled,
        className,
      )}
    >
      {control}
      <span className={styles.choiceText}>
        <label htmlFor={id} className={styles.choiceLabel}>
          {label}
        </label>
        {description && (
          <span id={descriptionId} className={styles.choiceDescription}>
            {description}
          </span>
        )}
      </span>
    </div>
  );
}
